import React, { useMemo } from 'react';
import ChartCard, {
    ChartCardContent as CardContent,
    ChartCardHeader as CardHeader,
    ChartCardTitle as CardTitle,
} from '../charts/ChartCard';
import EChart from '../charts/EChart';
import { useChartPalette } from '../charts/useChartMode';
import { ChartPalette, sentimentSliceColor } from '../charts/chartTheme';
import { donutOption } from '../charts/options';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from '../ui/table';

export interface DimensionDataItem {
    value: string;
    rawValue?: string;
    impressions: number;
    uniqueUrls: number;
    percentage: number;
}

// Categorical hues are assigned in fixed order and never cycled: past the last
// validated slot everything folds into a single grey "Others" slice.
const MAX_DONUT_SLICES = 6;
const OTHERS_LABEL = 'Others';
// Sentiment is the one dimension whose slice colours carry meaning rather than
// slot order: teal Positive / pink Negative, matching the AI-insights donut.
const SENTIMENT_DIMENSION = 'sentiment';

interface DimensionCardProps {
    title: string;
    data: DimensionDataItem[];
    loading?: boolean;
    onFilterClick?: (dimension: string, value: string) => void;
    dimension: string;
    chartType?: 'bar' | 'donut';
}

const DimensionCard: React.FC<DimensionCardProps> = ({
    title,
    data,
    loading = false,
    onFilterClick,
    dimension,
    chartType = 'bar',
}) => {
    if (loading) return <LoadingCard title={title} />;

    const chartData = data.slice(0, 8);

    return (
        <ChartCard>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
            </CardHeader>
            <CardContent>
                {chartType === 'donut' ? (
                    <>
                        <DimensionDonutChart
                            data={chartData}
                            dimension={dimension}
                            onFilterClick={onFilterClick}
                            title={title}
                        />
                        <DataTable data={chartData} dimension={dimension} onFilterClick={onFilterClick} />
                    </>
                ) : chartData.length === 1 ? (
                    <StatLine item={chartData[0]} />
                ) : (
                    <BarTable data={chartData} dimension={dimension} onFilterClick={onFilterClick} />
                )}
            </CardContent>
        </ChartCard>
    );
};

function LoadingCard({ title }: { title: string }) {
    return (
        <ChartCard>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-3 animate-pulse">
                    <div className="h-[200px] bg-canvas rounded-md" />
                    <div className="space-y-2">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-4 bg-canvas rounded w-full" />
                        ))}
                    </div>
                </div>
            </CardContent>
        </ChartCard>
    );
}

function DimensionDonutChart({
    data,
    dimension,
    onFilterClick,
    title,
}: {
    data: DimensionDataItem[];
    dimension: string;
    onFilterClick?: (dimension: string, value: string) => void;
    title: string;
}) {
    const palette = useChartPalette();

    const option = useMemo(() => {
        const slices = foldIntoOthers(data, dimension, palette);
        const total = data.reduce((sum, item) => sum + item.impressions, 0);
        return donutOption({
            palette,
            slices,
            centerValue: formatCompact(total),
            centerLabel: 'Impressions',
            valueFormatter: formatCompact,
            showLegend: true,
            height: 220,
        });
    }, [data, dimension, palette]);

    const onEvents = useMemo(
        () => ({
            click: (params: unknown) => {
                const { name } = params as { name: string };
                if (name === OTHERS_LABEL) return;
                const entry = data.find((item) => item.value === name);
                if (entry) onFilterClick?.(dimension, entry.rawValue ?? entry.value);
            },
        }),
        [data, dimension, onFilterClick],
    );

    return <EChart option={option} height={220} onEvents={onEvents} ariaLabel={`${title} share of impressions`} />;
}

function foldIntoOthers(data: DimensionDataItem[], dimension: string, palette: ChartPalette) {
    const named = data.filter((item) => item.value !== OTHERS_LABEL);
    const head = named.slice(0, MAX_DONUT_SLICES);
    const tail = [...named.slice(MAX_DONUT_SLICES), ...data.filter((item) => item.value === OTHERS_LABEL)];
    const isSentiment = dimension === SENTIMENT_DIMENSION;

    const slices = head.map((item, i) => ({
        name: item.value,
        value: item.impressions,
        color: isSentiment ? sentimentSliceColor(item.value, palette) : palette.categorical[i],
    }));

    if (tail.length > 0) {
        slices.push({
            name: OTHERS_LABEL,
            value: tail.reduce((sum, item) => sum + item.impressions, 0),
            color: palette.status.neutral,
        });
    }

    return slices;
}

function DataTable({
    data,
    dimension,
    onFilterClick,
}: {
    data: DimensionDataItem[];
    dimension: string;
    onFilterClick?: (dimension: string, value: string) => void;
}) {
    return (
        <div className="mt-3 overflow-x-auto">
            <Table className="text-xs">
                <TableHead>
                    <tr>
                        <TableHeaderCell>Value</TableHeaderCell>
                        <TableHeaderCell className="text-right">Impressions</TableHeaderCell>
                        <TableHeaderCell className="text-right">URLs</TableHeaderCell>
                        <TableHeaderCell className="text-right">%</TableHeaderCell>
                    </tr>
                </TableHead>
                <TableBody>
                    {data.map((row, index) => (
                        <DataTableRow key={row.value} row={row} dimension={dimension} onFilterClick={onFilterClick} />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function DataTableRow({
    row,
    dimension,
    onFilterClick,
}: {
    row: DimensionDataItem;
    dimension: string;
    onFilterClick?: (dimension: string, value: string) => void;
}) {
    return (
        <TableRow
            className="cursor-pointer hover:bg-ghost-bg"
            onClick={() => onFilterClick?.(dimension, row.rawValue ?? row.value)}
        >
            <TableCell className="py-1.5">{row.value}</TableCell>
            <TableCell className="py-1.5 text-right tabular-nums">{formatCompact(row.impressions)}</TableCell>
            <TableCell className="py-1.5 text-right tabular-nums">{row.uniqueUrls.toLocaleString()}</TableCell>
            <TableCell className="py-1.5 text-right tabular-nums">{row.percentage.toFixed(1)}%</TableCell>
        </TableRow>
    );
}

function BarTable({
    data,
    dimension,
    onFilterClick,
}: {
    data: DimensionDataItem[];
    dimension: string;
    onFilterClick?: (dimension: string, value: string) => void;
}) {
    const palette = useChartPalette();

    return (
        <div className="mt-3 overflow-x-auto">
            <Table className="text-xs">
                <TableHead>
                    <tr>
                        <TableHeaderCell>Value</TableHeaderCell>
                        <TableHeaderCell className="text-right">Impressions</TableHeaderCell>
                        <TableHeaderCell className="text-right">URLs</TableHeaderCell>
                        <TableHeaderCell className="text-right">%</TableHeaderCell>
                    </tr>
                </TableHead>
                <TableBody>
                    {data.map((row, index) => (
                        <BarTableRow
                            key={row.value}
                            row={row}
                            dimension={dimension}
                            onFilterClick={onFilterClick}
                            palette={palette}
                            index={index}
                        />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function BarTableRow({
    row,
    dimension,
    onFilterClick,
    palette,
    index,
}: {
    row: DimensionDataItem;
    dimension: string;
    onFilterClick?: (dimension: string, value: string) => void;
    palette: ChartPalette;
    index: number;
}) {
    const barColor =
        row.value === OTHERS_LABEL
            ? palette.status.neutral
            : index < palette.categorical.length
              ? palette.categorical[index]
              : palette.status.neutral;

    return (
        <TableRow
            className="cursor-pointer hover:bg-ghost-bg"
            onClick={() => onFilterClick?.(dimension, row.rawValue ?? row.value)}
        >
            <TableCell className="py-2" colSpan={4}>
                <div className="flex items-center gap-2.5 pb-1.5">
                    <span className="flex-1 font-semibold text-ink">{row.value}</span>
                    <span className="w-[70px] text-right tabular-nums text-ink">
                        {formatCompact(row.impressions)}
                    </span>
                    <span className="w-11 text-right tabular-nums text-ink-muted">
                        {row.uniqueUrls.toLocaleString()}
                    </span>
                    <span className="w-14 text-right tabular-nums text-ink-muted">{row.percentage.toFixed(1)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-canvas">
                    <div
                        data-testid="bar-fill"
                        className="h-1.5 rounded-full"
                        style={{ width: `${row.percentage}%`, backgroundColor: barColor }}
                    />
                </div>
            </TableCell>
        </TableRow>
    );
}

function StatLine({ item }: { item: DimensionDataItem }) {
    return (
        <div className="flex items-baseline gap-2.5">
            <span className="text-2xl font-bold text-ink">{formatCompact(item.impressions)}</span>
            <span className="text-sm font-semibold text-ink">{item.value}</span>
            <span className="text-xs text-ink-muted">
                100% of impressions · {item.uniqueUrls.toLocaleString()} URLs
            </span>
        </div>
    );
}

function formatCompact(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
    return String(value);
}

export default DimensionCard;
